import React, {ReactNode} from "react";
import moment from "moment";
import {Tooltip} from "@mui/material";
import {DeviceScheduleStatus} from "@barix/rpp-types";
import RppColors from "../../RppColors";


interface DeviceScheduleClockProps  {
    device: {
        online: boolean;
        status: {
            localTime: string;
            localTimeSyncTime?: Date;
            timeZone: string;
            scheduleStatus: DeviceScheduleStatus;
        }
    } | undefined,
    statusLocalTimeLabelI18N: string;
    style?: React.CSSProperties;
}

interface DeviceScheduleClockState  {
    msElapsed: number
}



class DeviceScheduleClock extends React.Component<DeviceScheduleClockProps, DeviceScheduleClockState> {

    private timerId: NodeJS.Timer | null = null;

    private divWrapperBaseStyle: React.CSSProperties = {
        border: `solid 0.16em ${RppColors.deviceDisabled()}`,
        borderRadius: "0.3em",
        width: "max-content",
        padding: "0.15em 0.2em 0.04em",
        textAlign: "center",
        userSelect: "none",
        lineHeight: "1",
        fontSize: "0.5em",
        fontFamily: "monospace",
    }


    public constructor(props: DeviceScheduleClockProps, context: any) {
        super(props, context);

        this.state = {
            msElapsed: (new Date()).getTime(),
        }
    }


    /**
     * Starts the watcher to main organization selector
     */
    public componentDidMount(): void {
        this.timerId = setInterval(() => {
            this.setState({msElapsed: (new Date()).getTime()})
        }, 5000);
    }


    public componentWillUnmount(): void {
        if (this.timerId) {
            clearInterval(this.timerId);
        }
    }


    public render(): ReactNode {

        return (
            <div style={{...this.props.style}}>
                {this.props.device && (
                    <Tooltip
                        title={this.getIconClickTitle()}>
                        <div style={{
                            ...this.divWrapperBaseStyle,
                            ...DeviceScheduleClock.getIconScheduleStyle(this.props.device)
                        }}>
                            {this.getHourAndMinutes()}
                        </div>
                    </Tooltip>
                )}
                {!this.props.device && (
                    <div style={this.divWrapperBaseStyle}>
                        --:--
                    </div>
                )}
            </div>
        );
    }


    private getActualTime(): Date {
        if (this.props.device.status.localTime && this.props.device.status.localTime.charAt(10) === "T") {
            const sanitizedLocalStr = this.props.device.status.localTime.substring(0, 19);
            if (this.props.device.online && this.props.device.status.localTimeSyncTime) {
                const timeDiffSecs = ((new Date()).getTime() / 1000) - ((new Date(this.props.device.status.localTimeSyncTime)).getTime() / 1000);
                return this.addSecondsToISODateString(sanitizedLocalStr, timeDiffSecs);
            } else {
                return new Date(sanitizedLocalStr);
            }
        }
        return null;
    }


    private getIconClickTitle(): NonNullable<React.ReactNode> {

        if (this.props.device && this.props.device.status) {

            let fullTimeNode: ReactNode;
            let timeZone: ReactNode = (<span/>);

            let localTime = "n/a";
            const actualTime = this.getActualTime();
            if (actualTime) {
                localTime = actualTime.toISOString().substring(0, 16);
                localTime = localTime.replace(/T/, " ");
            }

            fullTimeNode = (<span>{localTime}h</span>);

            if (this.props.device.status.timeZone && this.props.device.status.timeZone.length > 0) {
                timeZone = (<span>{this.props.device.status.timeZone}</span>);
            }

            let scheduleStatusPrefix;
            if (!this.props.device.online) {
                scheduleStatusPrefix = "Last schedule state: ";
            } else {
                scheduleStatusPrefix = "Schedule state: ";
            }
            const scheduleStatusTitle = scheduleStatusPrefix + (this.props.device.status.scheduleStatus ? this.props.device.status.scheduleStatus : "Unknown");

            return (
                <React.Fragment>
                    {this.props.device.online && (
                        <React.Fragment>
                            <span>{scheduleStatusTitle}</span><br/>
                        </React.Fragment>
                    )}
                    <div style={{whiteSpace: "nowrap"}}>
                        <span>{this.props.statusLocalTimeLabelI18N}:<br/> {fullTimeNode} {timeZone}</span>
                    </div>
                </React.Fragment>
            );

        } else {
            return "";
        }

    }


    private getHourAndMinutes(): NonNullable<React.ReactNode> {
        let localTime = "--:--";
        if (this.props.device && this.props.device.status) {
            const actualTime = this.getActualTime();
            if (actualTime) {
                localTime = DeviceScheduleClock.extractHHmmFromLocalTime(actualTime.toISOString());
            }
        }
        return localTime;
    }


    private static extractHHmmFromLocalTime(localTime: string): string {
        let hhmm = "--:--";
        // 2021-04-29T12:26:21+02:00
        if (localTime && localTime.charAt(10) === "T") {
            hhmm = localTime.substring(11, 16);
        }
        return hhmm;
    }


    private static getIconScheduleStyle(device: { online: boolean, status: { scheduleStatus: DeviceScheduleStatus } } | undefined): React.CSSProperties {
        if (device && device.online) {
            if (device.status && device.status.scheduleStatus && device.status.scheduleStatus === "Active") {
                return {borderColor: RppColors.deviceOnline(), color: RppColors.deviceOnline()};
            }
            return {borderColor: RppColors.deviceOffline(), color: RppColors.deviceOffline()};
        }
        return {borderColor: RppColors.deviceDisabled(), color: RppColors.deviceDisabled()};
    }


    /**
     * Add seconds to a Date ISO string.
     * @param isoDateStr {string} Reference date in ISO format
     * @param addSeconds {number} seconds to add
     * @return {string} format "YYYY-MM-DD HH:mm:ss"
     */
    private addSecondsToISODateString(isoDateStr: string, addSeconds: number): Date {
        const toMoment = moment(isoDateStr);
        const secondsOffset = isoDateStr.substring(19, 20) === "Z" || isoDateStr.substring(19, 24) === "+00:00" ? 0 : toMoment.utcOffset() * 60;
        // Utils.aLog(this, "isoDateStr", isoDateStr, "secondsOffset", secondsOffset);
        const value = moment(isoDateStr).add(addSeconds + secondsOffset, "seconds");
        // const isoStr = value.toISOString();
        // return isoStr.substring(11, 19);
        return value.toDate();
    }

}


export default DeviceScheduleClock;
